import { calculateMonthlyPayment, STANDARD_DEDUCTIONS } from '../calculations'

const MORTGAGE_INTEREST_LOAN_LIMIT = 750000
const SALT_CAP = 10000

interface RentVsBuyInput {
  monthlyRent: number
  homePrice: number
  downPayment: number
  mortgageRate: number // annual %
  loanTerm: number // months
  propertyTaxRate: number // annual %
  annualInsurance: number
  maintenancePercent: number // annual % of home value
  rentGrowthRate: number // annual %
  homeAppreciationRate: number // annual %
  investmentReturnRate: number // annual %
  timeHorizon: number // years
  filingStatus?: string
  marginalTaxRate?: number // %
  otherItemizedDeductions?: number
}

export function calculateRentVsBuy(input: RentVsBuyInput) {
  const loanAmount = input.homePrice - input.downPayment
  const monthlyMortgage = calculateMonthlyPayment(loanAmount, input.mortgageRate, input.loanTerm)
  const filingStatus = input.filingStatus || 'single'
  const marginalTaxRate = input.marginalTaxRate ?? 22
  const otherItemizedDeductions = input.otherItemizedDeductions ?? 0
  const standardDeduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single

  const monthlyPropertyTax = (input.homePrice * input.propertyTaxRate / 100) / 12
  const monthlyInsurance = input.annualInsurance / 12
  const monthlyMaintenance = (input.homePrice * input.maintenancePercent / 100) / 12

  const yearlyData = []
  let rentBalance = 0 // renter's investment portfolio (down payment + monthly savings)
  let buyEquity = input.downPayment
  let loanBalance = loanAmount
  let cumulativeRentCost = 0
  let cumulativeBuyCost = input.downPayment // upfront cost
  let currentRent = input.monthlyRent
  let currentHomeValue = input.homePrice
  let breakEvenYear = null
  let cumulativeTaxBenefit = 0

  // Renter invests the down payment upfront
  rentBalance = input.downPayment

  const monthlyInvestReturn = input.investmentReturnRate / 100 / 12
  const monthlyMortgageRate = input.mortgageRate / 100 / 12

  // Interest deduction ratio: if loan exceeds $750k limit, only a portion is deductible
  const interestDeductionRatio = Math.min(1, MORTGAGE_INTEREST_LOAN_LIMIT / Math.max(1, loanAmount))

  for (let year = 1; year <= input.timeHorizon; year++) {
    let yearRentCost = 0
    let yearBuyCost = 0
    let yearlyInterest = 0

    for (let month = 0; month < 12; month++) {
      // Renter: pay rent, invest the difference
      yearRentCost += currentRent
      const buyMonthlyTotal = monthlyMortgage + monthlyPropertyTax + monthlyInsurance + monthlyMaintenance
      const monthlySavings = buyMonthlyTotal - currentRent

      // If renting is cheaper, invest the difference
      if (monthlySavings > 0) {
        rentBalance = rentBalance * (1 + monthlyInvestReturn) + monthlySavings
      } else {
        // If buying is cheaper, renter still grows existing portfolio
        rentBalance = rentBalance * (1 + monthlyInvestReturn)
      }

      // Buyer: pay mortgage + extras
      yearBuyCost += buyMonthlyTotal

      // Mortgage amortization
      const interest = loanBalance * monthlyMortgageRate
      const principal = monthlyMortgage - interest
      loanBalance = Math.max(0, loanBalance - principal)
      yearlyInterest += interest
    }

    // Annual adjustments
    currentRent *= (1 + input.rentGrowthRate / 100)
    currentHomeValue *= (1 + input.homeAppreciationRate / 100)

    cumulativeRentCost += yearRentCost
    cumulativeBuyCost += yearBuyCost

    buyEquity = currentHomeValue - loanBalance

    // Tax benefit calculation
    const annualPropertyTax = input.homePrice * input.propertyTaxRate / 100
    const deductibleInterest = yearlyInterest * interestDeductionRatio
    const deductibleSALT = Math.min(annualPropertyTax, SALT_CAP)
    const totalItemized = deductibleInterest + deductibleSALT + otherItemizedDeductions
    const taxBenefit = Math.max(0, totalItemized - standardDeduction) * (marginalTaxRate / 100)
    cumulativeTaxBenefit += taxBenefit

    // Renter net wealth: investment portfolio
    // Buyer net wealth: home equity (minus ~6% selling costs) + cumulative tax benefit
    const sellingCosts = currentHomeValue * 0.06
    const buyerNetWealth = buyEquity - sellingCosts + cumulativeTaxBenefit
    const renterNetWealth = rentBalance

    yearlyData.push({
      year,
      rentMonthly: Math.round(currentRent),
      cumulativeRentCost: Math.round(cumulativeRentCost),
      cumulativeBuyCost: Math.round(cumulativeBuyCost),
      renterNetWealth: Math.round(renterNetWealth),
      buyerNetWealth: Math.round(buyerNetWealth),
      homeValue: Math.round(currentHomeValue),
      loanBalance: Math.round(loanBalance),
      buyEquity: Math.round(buyEquity),
      rentPortfolio: Math.round(rentBalance),
      taxBenefit: Math.round(taxBenefit),
      cumulativeTaxBenefit: Math.round(cumulativeTaxBenefit),
      yearlyInterest: Math.round(yearlyInterest),
    })

    if (breakEvenYear === null && buyerNetWealth >= renterNetWealth) {
      breakEvenYear = year
    }
  }

  const finalData = yearlyData[yearlyData.length - 1]
  const buyerWins = finalData ? finalData.buyerNetWealth > finalData.renterNetWealth : false
  const advantage = finalData ? Math.abs(finalData.buyerNetWealth - finalData.renterNetWealth) : 0

  return {
    monthlyMortgage: Math.round(monthlyMortgage),
    monthlyPropertyTax: Math.round(monthlyPropertyTax),
    monthlyInsurance: Math.round(monthlyInsurance),
    monthlyMaintenance: Math.round(monthlyMaintenance),
    totalBuyMonthly: Math.round(monthlyMortgage + monthlyPropertyTax + monthlyInsurance + monthlyMaintenance),
    monthlyRent: input.monthlyRent,
    yearlyData,
    breakEvenYear,
    buyerWins,
    advantage: Math.round(advantage),
    finalRenterWealth: finalData?.renterNetWealth || 0,
    finalBuyerWealth: finalData?.buyerNetWealth || 0,
    totalTaxBenefit: Math.round(cumulativeTaxBenefit),
  }
}
